import { AccountInfo, PublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { CONSTANT } from '@const';
import { UserIdentityType, WaterIQClaims, WaterIQUserType } from '@types';
import { getCurrentDateTime } from '@utils';
import { isAfter } from 'date-fns';

// Config
import { msalConfig, protectedResources } from '../authConfig';

const POLICY_ID_KEY = 'POLICY_ID';

export class MsalService {
	private msalInstance: PublicClientApplication | null = null;
	private msalInitialized = false;
	private accessTokenExpiration: number | undefined;
	private lastTokenEndpointInteraction: number | undefined;

	constructor() {
		this.accessTokenExpiration = undefined; // there will not be a claims token to assign from at this stage yet
		this.lastTokenEndpointInteraction = undefined; // there will not be a claims token to assign from at this stage yet
	}

	/**
	 * Gets the single PublicClientApplication (MSAL) instance
	 * Lazily initializes it on first access to avoid SSR issues
	 * @returns {PublicClientApplication} instance of MSAL
	 */
	public getMsalInstance() {
		if (typeof window === 'undefined') {
			// Return a mock during SSR to prevent errors
			return null as any;
		}
		
		if (!this.msalInstance) {
			this.msalInstance = new PublicClientApplication(msalConfig);
		}
		return this.msalInstance;
	}

	/**
	 * Initializes the MSAL instance. Must be called and awaited before
	 * using methods that depend on the initialized instance (e.g. getAccount).
	 */
	public async initialize(): Promise<void> {
		const instance = this.getMsalInstance();
		if (!instance) return;
		await instance.initialize();
		this.msalInitialized = true;
	}

	/**
	 * Sets the authority policy in storage.
	 * @param {string} policyId The authoricy policy ID to be set.
	 */
	public setAuthorityPolicy(policyId: string) {
		localStorage.setItem(POLICY_ID_KEY, policyId);
	}

	/**
	 * Gets the token silently from MSAL session that can be used in API call.
	 * If the access token has expired, it attempts to use the refresh token to get a new one.
	 * If that fails, it throws an error.
	 * @returns {Promise<string | null>} accessToken. Otherwise, returns null value if the call fails or token is expired
	 * @throws {Error} If token refresh fails
	 */
	public async getToken(): Promise<string | null> {
		const account = this.getAccount();
		if (account && this.msalInstance) {
			const authorityPolicyOnRuntime = this.getAuthorityPolicy();
			const tokenRequest: SilentRequest = {
				scopes: protectedResources.apiHello.scopes,
				account: account,
				authority: authorityPolicyOnRuntime, // if undefined, default authority in msal config will be used
			};

			try {
				const token = await this.msalInstance.acquireTokenSilent(tokenRequest);
				this.accessTokenExpiration = token.expiresOn
					? token.expiresOn.getTime()
					: this.getTokenExpirationTime();
				this.lastTokenEndpointInteraction = Date.now();
				return token.accessToken ? token.accessToken : null;
			} catch (error) {
				console.error('Error acquiring token:', error);
				throw error;
			}
		}

		return null;
	}

	/**
	 * Checks whether the user account is valid
	 * @returns {boolean} return true if user account is valid, false otherwise
	 */
	public isValidAccount() {
		const account = this.getAccount();
		if (!account) {
			return false;
		}

		const claims = this.getClaims(account);
		return (
			this.validateUser(claims) &&
			this.validateExpirationTime(claims) &&
			this.validateAudience(claims) &&
			this.validateIssuer(claims) &&
			this.validateSecurityLevel(claims) &&
			this.validateUserTypes(claims)
		);
	}

	/**
	 * Checks whether the user account is valid when is not linked to an external identity contact
	 * @returns {boolean} return true if unlinked user account is valid, false otherwise
	 */
	public isValidUnlinkedAccount() {
		const account = this.getAccount();
		if (!account) {
			return false;
		}

		const claims = this.getClaims(account);

		return (
			this.validateUser(claims) &&
			this.validateExpirationTime(claims) &&
			this.validateAudience(claims) &&
			this.validateIssuer(claims) &&
			this.validateSecurityLevel(claims) &&
			!this.validateUserTypes(claims)
		);
	}

	/**
	 * Checks whether the user account is not valid by user but valid by all other terms (excluding user types)
	 * @returns {boolean} return true if is closed account is valid, false otherwise
	 */
	public isClosedAccount() {
		const account = this.getAccount();
		if (!account) {
			return false;
		}

		const claims = this.getClaims(account);

		return (
			!this.validateUser(claims) &&
			this.validateExpirationTime(claims) &&
			this.validateAudience(claims) &&
			this.validateIssuer(claims) &&
			this.validateSecurityLevel(claims)
		);
	}

	/**
	 * Checks whether the user account has too low a privilege level
	 * @returns {boolean} return true if user account has a BASIC security level, false otherwise
	 */
	public isPrivilegeLevelTooLow() {
		const account = this.getAccount();
		if (!account) {
			return true;
		}
		const claims = this.getClaims(account);
		return Boolean(!this.validateSecurityLevel(claims));
	}

	/**
	 * Validates current time is chronologically between the time token became valid
	 * (usually when token was issued) and the expiration time, both including.
	 * @param {AccountInfo} account: user account information
	 */
	public validateTokenLifetime(expirationTime: number) {
		const expireTime = new Date(0);
		expireTime.setUTCSeconds(
			expirationTime && Number.isFinite(expirationTime) ? expirationTime : 0,
		);
		return isAfter(expireTime, new Date(getCurrentDateTime()));
	}

	/**
	 * Checks whether the user account has any valid user type (DQP, WEH)
	 * @param {WaterIQClaims} [claims] claims from WaterIQ token
	 * @returns {boolean}
	 */
	public validateUserTypes(claims?: WaterIQClaims) {
		if (!claims) {
			const account = this.getAccount();
			if (!account) {
				return false;
			}
			claims = this.getClaims(account);
		}
		return claims.wateriq_usertype?.some(item =>
			[WaterIQUserType.DQP, WaterIQUserType.WEH].includes(item),
		);
	}

	/**
	 * Get stocked token expiration time
	 * @returns {number} expiration time
	 */
	public getTokenExpiration() {
		if (!this.accessTokenExpiration) {
			this.accessTokenExpiration = this.getTokenExpirationTime(); // initialize account expiration time
		}
		return this.accessTokenExpiration;
	}

	/**
	 * Get the time, in milliseconds since the token endpoint was called - TUO has an OIDC session expiry of 30 minutes
	 * @returns {number | undefined} milliseconds since the token endpoint was called
	 */
	public getLastTokenEndpointInteraction() {
		return this.lastTokenEndpointInteraction
			? Date.now() - this.lastTokenEndpointInteraction
			: undefined;
	}

	/**
	 * Retrieves the contact id among the token claims of the first account found in cache
	 * @returns {string} contact id
	 */
	public getClaimsContactId() {
		const account = this.getAccount();
		if (!account) {
			return '';
		}
		const claims = this.getClaims(account);
		// console.log(claims);
		return claims.contactId;
	}

	/**
	 * Extract the user identity from the token claims of the first account found in cache
	 * @returns {UserIdentityType} user identity object
	 */
	public getUserIdentity() {
		const account = this.getAccount();
		if (account) {
			const waterIQClaims = this.getClaims(account);
			const userIdentity = this.mapWaterIQClaimsToUserIdentity(waterIQClaims);
			return userIdentity;
		}
	}

	/**
	 * Extract the user type from the token claims of the first account found in cache
	 * @returns {WaterIQUserType} WaterIQ user type
	 */
	public getUserTypes() {
		const account = this.getAccount();
		if (account) {
			const waterIQClaims = this.getClaims(account);
			return waterIQClaims.wateriq_usertype;
		}
	}

	/**
	 * Retrieves the authority policy from storage
	 * @returns {string | undefined} authority policy id or undefined if none is found
	 */
	private getAuthorityPolicy() {
		return localStorage.getItem(POLICY_ID_KEY) ?? undefined;
	}

	/**
	 * Retrieves the token expiration time based on the account's claims
	 * @returns {number}
	 */
	private getTokenExpirationTime() {
		const account = this.getAccount();
		if (!account) {
			return 0;
		}
		const claims = this.getClaims(account);
		return claims.exp;
	}

	/**
	 * Calls getAllAccounts and determines the first account found in cache
	 * @returns {AccountInfo | null} first account found, null otherwise
	 */
	private getAccount(): AccountInfo | undefined {
		if (!this.msalInstance || !this.msalInitialized) return undefined;
		
		const currentAccounts = this.msalInstance.getAllAccounts();

		if (currentAccounts && currentAccounts.length > 0) {
			return currentAccounts[0];
		}

		return undefined;
	}

	/**
	 * Retrieves the token claims
	 * @param {AccountInfo} account: user account information
	 * @returns {WaterIQClaims} claims from WaterIQ token
	 */
	private getClaims(account: AccountInfo): WaterIQClaims {
		return account.idTokenClaims as WaterIQClaims;
	}

	private validateUser(claims: WaterIQClaims) {
		return Boolean(claims.isapprovedexternaluser);
	}

	private validateAudience(claims: WaterIQClaims) {
		return claims.aud == CONSTANT.APPLICATION_ID;
	}

	private validateIssuer(claims: WaterIQClaims) {
		return claims.iss == CONSTANT.SECURITY_TOKEN_SERVICE_ISSUER;
	}

	private validateExpirationTime(claims: WaterIQClaims) {
		const epoch = claims.exp;
		return this.validateTokenLifetime(epoch);
	}

	private validateSecurityLevel(claims: WaterIQClaims) {
		const permittedLevels = [CONSTANT.SECURITY_LEVEL_STANDARD, CONSTANT.SECURITY_LEVEL_STRONG];
		const userLevel = claims.privilegelvl;
		return Boolean(userLevel && permittedLevels.includes(userLevel));
	}

	private mapWaterIQClaimsToUserIdentity(claims: WaterIQClaims): UserIdentityType {
		return {
			userId: claims.contactId,
			firstName: claims.given_name,
			lastName: claims.family_name,
			email: claims.email,
			birthdate: claims.birthdate,
		};
	}

	/**
	 * Forces a token refresh by acquiring a new token with forceRefresh flag
	 * @returns {Promise<string | null>} New access token or null if failed
	 */
	public async forceTokenRefresh(): Promise<string | null> {
		const account = this.getAccount();
		if (!account || !this.msalInstance) {
			return null;
		}

		try {
			const authorityPolicyOnRuntime = this.getAuthorityPolicy();
			const tokenRequest: SilentRequest = {
				scopes: protectedResources.apiHello.scopes,
				account: account,
				authority: authorityPolicyOnRuntime,
				forceRefresh: true, // force MSAL to refresh the token
			};

			const token = await this.msalInstance.acquireTokenSilent(tokenRequest);

			this.accessTokenExpiration = token.expiresOn
				? token.expiresOn.getTime()
				: this.getTokenExpirationTime();
			this.lastTokenEndpointInteraction = Date.now();

			return token.accessToken ?? null;
		} catch (error) {
			console.error('Error forcing token refresh:', error);
			throw error;
		}
	}

	public logAccountValidations() {
		if (!CONSTANT.IS_DEV) {
			return;
		}
		const account = this.getAccount();
		if (!account) {
			return false;
		}
		this.logAccountValidation(account);
	}

	public logAccountValidation(account: AccountInfo) {
		if (!CONSTANT.IS_DEV) {
			return;
		}
		const claims = this.getClaims(account);
		const isValidUser = this.validateUser(claims);
		const isValidExpirationTime = this.validateExpirationTime(claims);
		const isValidAudience = this.validateAudience(claims);
		const isValidIssuer = this.validateIssuer(claims);
		const isValidSecurityLevel = this.validateSecurityLevel(claims);
		// eslint-disable-next-line no-restricted-syntax
		console.group('WaterIQ Application Authorisation (account validations)');
		console.info('%cUser: ' + isValidUser, `color: ${isValidUser ? 'green' : 'red'}`);
		const expire = new Date(claims.exp * 1000);
		console.info(
			'%cExpirationTime: ' + expire,
			`color: ${isValidExpirationTime ? 'green' : 'red'}`,
		);
		console.info('%cAudience: ' + isValidAudience, `color: ${isValidAudience ? 'green' : 'red'}`);
		console.info('%cIssuer: ' + isValidIssuer, `color: ${isValidIssuer ? 'green' : 'red'}`);
		console.info(
			'%cSecurityLevel: ' + isValidSecurityLevel,
			`color: ${isValidSecurityLevel ? 'green' : 'red'}`,
		);

		// eslint-disable-next-line no-restricted-syntax
		console.groupEnd();
	}
}
